import SCRIPTS from './productDemoScripts'

// Homepage product demos. A fake cursor clicks through a static replica of the
// app (app/views/welcome/demos/*); data-demo names the script it runs.
// Anything the cursor can reach carries data-t; view state lives in data
// attributes that scripts flip and CSS styles. Loops while on screen, stops
// when scrolled away, skipped for reduced motion. Each loop restores the
// screen from an untouched <template> copy.
class Cancelled extends Error {}

const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2)

class ProductDemo {
  constructor(element) {
    this.element = element
    this.screen = element.querySelector('[data-demo-screen]')
    this.cursor = element.querySelector('[data-demo-cursor]')
    this.template = element.querySelector('template[data-demo-template]')
    this.script = SCRIPTS[element.dataset.demo]
    this.pos = { x: 0, y: 0 }
    this.observer = new IntersectionObserver(
      ([entry]) => (entry.isIntersecting ? this.start() : this.stop()),
      { threshold: 0.3 }
    )
    this.observer.observe(element)
  }

  start() {
    if (this.run) return
    this.run = {}
    this.frame = requestAnimationFrame(this.tick)
    ;(async () => {
      try {
        for (;;) {
          this.reset()
          await this.wait(500)
          await this.script(this)
        }
      } catch (e) {
        if (!(e instanceof Cancelled)) throw e
      }
    })()
  }

  stop() {
    this.run = null
    cancelAnimationFrame(this.frame)
    this.hovered?.removeAttribute('data-hover')
    this.hovered = null
  }

  reset() {
    this.screen.replaceChildren(this.template.content.cloneNode(true))
    for (const key of Object.keys(this.screen.dataset)) {
      if (key !== 'demoScreen') delete this.screen.dataset[key]
    }
    this.hovered = null
    this.motion = null
    const box = this.element.getBoundingClientRect()
    this.pos = { x: box.width * 0.82, y: box.height + 30 }
    this.place()
  }

  // --- what scripts call ----------------------------------------------------

  find(selector) {
    return this.screen.querySelector(selector)
  }

  findAll(selector) {
    return this.screen.querySelectorAll(selector)
  }

  // Screen-level state, e.g. set('view', 'room') → data-view="room".
  set(key, value) {
    this.screen.dataset[key] = value
  }

  unset(key) {
    delete this.screen.dataset[key]
  }

  url(text) {
    this.find('[data-url]').textContent = text
  }

  // Smooth-scrolls the visible [data-scroll] pane so [data-t=name] sits near
  // its top, then waits for the scroll to settle.
  async scrollTo(name, offset = 16) {
    const el = this.find(`[data-t="${name}"]`)
    const pane = el.closest('[data-scroll]')
    const top = pane.scrollTop + el.getBoundingClientRect().top - pane.getBoundingClientRect().top - offset
    pane.scrollTo({ top, behavior: 'smooth' })
    await this.wait(700)
  }

  wait(ms) {
    const run = this.run
    return new Promise((resolve, reject) =>
      setTimeout(() => (run && this.run === run ? resolve() : reject(new Cancelled())), ms)
    )
  }

  async clickOn(name, offset) {
    await this.move(name, offset)
    await this.click()
  }

  // Curved, eased path to the centre of [data-t=name]; bows to a random side
  // and takes longer for longer trips, which is most of what makes it feel human.
  async move(name, { dx = 0, dy = 0 } = {}) {
    const el = this.find(`[data-t="${name}"]`)
    if (!el) return
    const box = this.element.getBoundingClientRect()
    const r = el.getBoundingClientRect()
    const from = { ...this.pos }
    const to = { x: r.left - box.left + r.width / 2 + dx, y: r.top - box.top + r.height / 2 + dy }
    const ox = to.x - from.x
    const oy = to.y - from.y
    const dist = Math.hypot(ox, oy) || 1
    const bow = Math.min(60, dist * 0.12) * (Math.random() < 0.5 ? -1 : 1)
    const ctrl = { x: (from.x + to.x) / 2 - (oy / dist) * bow, y: (from.y + to.y) / 2 + (ox / dist) * bow }
    const ms = Math.round(Math.min(950, 280 + dist * 0.75))
    this.motion = { from, to, ctrl, ms, start: performance.now() }
    await this.wait(ms + 40)
  }

  async click() {
    const ring = document.createElement('span')
    ring.className = 'pd-click'
    ring.addEventListener('animationend', () => ring.remove())
    this.cursor.append(ring)
    this.cursor.classList.add('is-down')
    await this.wait(110)
    this.cursor.classList.remove('is-down')
    await this.wait(90)
  }

  // Types into a .pd-field, one character at a time with a little jitter.
  async type(selector, text, { ms = 55, from = '' } = {}) {
    const field = this.find(selector)
    field.dataset.typing = ''
    for (let i = 1; i <= text.length; i++) {
      field.textContent = from + text.slice(0, i)
      await this.wait(ms + Math.random() * 40)
    }
    delete field.dataset.typing
  }

  // --- cursor ---------------------------------------------------------------

  tick = (now) => {
    const m = this.motion
    if (m) {
      const t = Math.min(1, (now - m.start) / m.ms)
      const s = ease(t)
      const u = 1 - s
      this.pos.x = u * u * m.from.x + 2 * u * s * m.ctrl.x + s * s * m.to.x
      this.pos.y = u * u * m.from.y + 2 * u * s * m.ctrl.y + s * s * m.to.y
      if (t >= 1) this.motion = null
      this.place()
    }
    this.hover()
    this.frame = requestAnimationFrame(this.tick)
  }

  place() {
    this.cursor.style.transform = `translate(${this.pos.x}px, ${this.pos.y}px)`
  }

  // Fake hover: whatever [data-t] sits under the cursor gets data-hover, and
  // the cursor turns into a hand over it.
  hover() {
    const box = this.element.getBoundingClientRect()
    const under = document.elementFromPoint(box.left + this.pos.x, box.top + this.pos.y)
    const target = under && this.screen.contains(under) ? under.closest('[data-t]') : null
    if (target === this.hovered) return
    this.hovered?.removeAttribute('data-hover')
    target?.setAttribute('data-hover', '')
    this.hovered = target
    this.cursor.classList.toggle('is-pointer', !!target)
  }
}

document.addEventListener('DOMContentLoaded', () => {
  if (matchMedia('(prefers-reduced-motion: reduce)').matches) return
  document.querySelectorAll('[data-demo]').forEach((el) => new ProductDemo(el))
})
